import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TagIcon from '@mui/icons-material/Tag';
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

function InfoRow({ label, value }) {
    return (
        <Stack direction='row' spacing={2} alignItems='baseline'>
            <Typography sx={{ fontSize: '13px', color: 'subtext.main', minWidth: 130 }}>{label}</Typography>
            <Typography sx={{ fontSize: '14px' }}>{value || '—'}</Typography>
        </Stack>
    );
}

function ReviewContract({ contractData, rebatesData }) {
    const navigate = useNavigate();

    const hasRebates = rebatesData && rebatesData.length > 0;

    return (
        <Stack spacing={4} sx={{ maxWidth: 800, mx: 'auto', mt: 4, pb: 8 }}>
            {/* Header */}
            <Stack direction='row' spacing={1.5} alignItems='center'>
                <CheckCircleIcon sx={{ color: 'green.main', fontSize: 28 }} />
                <Stack spacing={0.25}>
                    <Typography variant='heavy' sx={{ fontSize: '24px' }}>
                        Contract Saved
                    </Typography>
                    <Typography sx={{ color: 'subtext.main', fontSize: '14px' }}>
                        Review the details below before returning to your contracts.
                    </Typography>
                </Stack>
            </Stack>

            {/* Contract details */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
                <Stack spacing={2}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <TagIcon sx={{ fontSize: 18, color: 'subtext.main' }} />
                        <Typography variant='heavy' sx={{ fontSize: '16px' }}>Contract Details</Typography>
                    </Stack>
                    <Divider />
                    <InfoRow label='Contract Name' value={contractData?.name} />
                    <InfoRow label='External ID' value={contractData?.external_id} />
                    <InfoRow label='Start Date' value={contractData?.start_date} />
                    <InfoRow label='Effective Start Date' value={contractData?.effective_start_date} />
                    <InfoRow label='End Date' value={contractData?.end_date} />
                </Stack>
            </Paper>

            {/* Rebates */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
                <Stack spacing={2}>
                    <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <CalendarTodayIcon sx={{ fontSize: 18, color: 'subtext.main' }} />
                            <Typography variant='heavy' sx={{ fontSize: '16px' }}>Rebates</Typography>
                        </Stack>
                        {hasRebates && (
                            <Chip label={`${rebatesData.length} rebate${rebatesData.length !== 1 ? 's' : ''}`} size='small' />
                        )}
                    </Stack>
                    <Divider />

                    {hasRebates ? (
                        <Stack spacing={2}>
                            {rebatesData.map((rebate, index) => (
                                <Box key={index}>
                                    <Typography variant='heavy' sx={{ fontSize: '13px', mb: 1 }}>
                                        Rebate {index + 1}{rebate.name ? ` — ${rebate.name}` : ''}
                                    </Typography>
                                    <Stack spacing={0.75} pl={1.5}>
                                        <InfoRow label='Period' value={({ monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Annual', contract_term: 'Rolling', custom: 'Custom', unknown: 'Custom' }[rebate.periodType])} />
                                        <InfoRow label='Start Date' value={rebate.startDate} />
                                        <InfoRow label='End Date' value={rebate.endDate} />
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Typography sx={{ fontSize: '14px', color: 'subtext.main', fontStyle: 'italic' }}>
                            No rebates added. You can configure rebates later from the contract page.
                        </Typography>
                    )}
                </Stack>
            </Paper>

            {/* Footer */}
            <Stack direction='row' justifyContent='flex-end'>
                <Button variant='blueContained' onClick={() => navigate('/contracts')}>
                    Back to All Contracts
                </Button>
            </Stack>
        </Stack>
    );
}

export default ReviewContract;
